
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from '@/lib/utils';
import { Briefcase, Calculator, ChevronDown, ChevronRight, CircleChevronRight, ExternalLink, Menu, UserRound, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [clickedMenu, setClickedMenu] = useState<string | null>(null);
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);
  const areaAgenteHref = (import.meta.env.VITE_AREA_AGENTE_BASE ?? "").trim() || "#";
  const minhaThopentHref = (import.meta.env.VITE_MINHA_THOPEN_URL ?? "https://rzk-homolog.acessoenergia.com.br/auth/login").trim();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setClickedMenu(null);
      }
    };

    if (clickedMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [clickedMenu]);

  const menuItems = [
    {
      name: 'Soluções',
      path: '/solucoes',
        submenu: {
          categories: {
            'o-que-precisa': {
              name: 'O Que Você Precisa?',
              items: [
                {
                  name: 'Quero Economizar na Conta de Luz',
                  items: [
                    { name: 'Para Sua Casa', path: '/solucoes/o-que-voce-precisa/quero-economizar-na-conta-de-luz/para-sua-casa' },
                    { name: 'Para Sua Empresa', path: '/solucoes/o-que-voce-precisa/quero-economizar-na-conta-de-luz/para-sua-empresa' },
                    { name: 'Simule sua Economia', path: '/solucoes/o-que-voce-precisa/quero-economizar-na-conta-de-luz/simule-sua-economia' }
                  ]
                }
              ]
            },
            'produtos-servicos': {
              name: 'Nossos Produtos e Serviços',
              items: [
                {
                  name: 'Geração Distribuída',
                  items: [
                    { name: 'Geração Compartilhada', path: '/solucoes/nossos-produtos-e-servicos/geracao-distribuida/geracao-compartilhada' },
                    { name: 'Geração Remota', path: '/solucoes/nossos-produtos-e-servicos/geracao-distribuida/geracao-remota' },
                    { name: 'Gestão Backoffice', path: '/solucoes/nossos-produtos-e-servicos/geracao-distribuida/gestao-backoffice' }
                  ]
                },
                {
                  name: 'Mercado Livre de Energia',
                  items: [
                    { name: 'Mercado Livre de Energia', path: '/solucoes/nossos-produtos-e-servicos/mercado-livre-de-energia' },
                    { name: 'Gestão Backoffice', path: '/solucoes/nossos-produtos-e-servicos/mercado-livre-de-energia/gestao-backoffice' }
                  ]
                },
                {
                  name: 'Gestão e Eficiência Energética',
                  items: [
                    { name: 'Diagnóstico de Contas de Energia', path: '/solucoes/gestao-e-eficiencia-energetica/diagnostico-de-contas-de-energia' },
                    { name: 'Ressarcimento de Valores Indevidos', path: '/solucoes/gestao-e-eficiencia-energetica/ressarcimento-de-valores-indevidos' },
                    { name: 'Gestão Completa de Faturas', path: '/solucoes/gestao-e-eficiencia-energetica/gestao-completa-de-faturas' }
                  ]
                }
              ]
            }
          }
        }
    },
    {
      name: 'Entenda sua energia',
      path: '/entenda-sua-energia',
      hasDropdown: true,
      simple: false,
      dropdownItems: [
        { name: 'Descomplique sua conta de luz', path: '/entenda-sua-energia/descomplique-sua-conta-de-luz' },
        { name: 'Benefícios da Energia Limpa', path: '/entenda-sua-energia/beneficios-da-energia-limpa' },
        { name: 'O Mercado Livre de Energia: guia completo', path: '/entenda-sua-energia/o-mercado-livre-de-energia-guia-completo' },
        { name: 'Como a Thopen gera economia na conta de luz', path: '/entenda-sua-energia/como-a-thopen-gera-economia-na-conta-de-luz' },
        // { name: 'Queda de energia', path: '/entenda-sua-energia/queda-de-energia' },
        { name: 'Qualidade do fornecimento', path: '/entenda-sua-energia/qualidade-do-fornecimento' },
        { name: 'Disponibilização da fatura', path: '/entenda-sua-energia/disponibilizacao-da-fatura' },
        // { name: 'Queima de equipamento', path: '/entenda-sua-energia/queima-de-equipamento' }
      ]
    },
    { 
      name: 'Quem Somos', 
      path: '/quem-somos',
      hasDropdown: true,
      simple: false,
      dropdownItems: [
        { name: 'Quem Somos', path: '/quem-somos' },
        { name: 'Nossas Usinas', path: '/quem-somos/nossas-usinas' },
        { name: 'Trabalhe Conosco', path: '/quem-somos/trabalhe-conosco' }
      ]
    },
    { 
      name: 'Insights', 
      path: '/insights',
      hasDropdown: true,
      simple: false,
      externalLinks: true,
      dropdownItems: [
        { name: 'Blog', path: 'https://blog.thopenergy.com.br', target:'_blank', rel: 'noopener noreferrer' },
        { name: 'Saiu na Mídia', path: 'https://blog.thopenergy.com.br/saiu-na-midia', target:'_blank', rel: 'noopener noreferrer' }
      ]
    }
];

  const microcopy: Record<string, string> = {
    "Geração Compartilhada": "Participe de uma usina e reduza sua conta com créditos de energia.",
    "Geração Remota": "Compre energia limpa de usinas à distância, sem obras no seu imóvel.",
    "Gestão Backoffice": "Operação, faturamento e compliance de ponta a ponta.",
    "Mercado Livre de Energia": "Energia no mercado livre para consumidores de todos os portes.",
    "Diagnóstico de Contas de Energia": "Encontramos oportunidades de economia nas suas faturas.",
    "Ressarcimento de Valores Indevidos": "Recupere valores pagos a mais conforme a regulação.",
    "Gestão Completa de Faturas": "Centralize, valide e pague suas faturas com controle.",
    "Certificados I-REC e Créditos de Carbono": "Comprove sua origem renovável e compense emissões.",
    
    "Para Sua Casa": "Soluções simples para reduzir a conta de luz residencial.",
    "Para Sua Empresa": "Economia e previsibilidade para o seu negócio.",
    "Simule sua Economia": "Faça uma simulação rápida e veja quanto pode economizar.",
    "Descomplique sua conta de luz": "Entenda cada item da sua fatura sem complicação.",
    "Benefícios da Energia Limpa": "Conheça os ganhos ambientais e financeiros.",
    "O Mercado Livre de Energia: guia completo": "Tudo sobre migração, contratos e riscos.",
    "Como a Thopen gera economia na conta de luz": "Veja como transformamos energia em resultado.",
    "Queda de energia": "Orientações e suporte para interrupções no fornecimento.",
    "Qualidade do fornecimento": "Ajuda em oscilações e variações de tensão.",
    "Disponibilização da fatura": "Suporte quando sua conta não é emitida ou entregue.",
    "Queima de equipamento": "Procedimentos para ressarcimento por danos elétricos."
  };

  const getMicrocopy = (name: string) => microcopy[name] || `Saiba mais sobre ${name.toLowerCase()}`;
  const flattenCategoryItems = (category: any) => category.items.flatMap((section: any) => section.items);

  return (
    <header className="bg-white shadow-sm border-b border-gray-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img
              src="/lovable-uploads/37042df7-136b-45af-ad35-e1168c599a3d.webp"
              alt="Thopen Energy"
              className="h-10 w-auto"
              width={500}
              height={152}
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1" ref={menuRef}>
            {menuItems.map((item) => (
              <div
                key={item.name}
                className="relative"
              >
                {item.simple ? (
                  <Link
                    to={item.path}
                    className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      location.pathname === item.path
                        ? 'text-thopen-pink bg-pink-50'
                        : 'text-gray-700 hover:text-thopen-pink hover:bg-gray-50'
                    }`}
                  >
                    {item.name}
                  </Link>
                ) : item.hasDropdown ? (
                  <div className="relative">
                    <button
                      onClick={() => {
                        if (clickedMenu === item.name) {
                          setClickedMenu(null);
                        } else {
                          setClickedMenu(item.name);
                        }
                      }}
                      className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        location.pathname.startsWith(item.path) || clickedMenu === item.name
                          ? 'text-thopen-pink bg-pink-50'
                          : 'text-gray-700 hover:text-thopen-pink hover:bg-gray-50'
                      }`}
                    >
                      {item.name}
                      <ChevronDown className="ml-1 h-4 w-4" />
                    </button>
                    
                    {/* Simple Dropdown */}
                    {clickedMenu === item.name && item.dropdownItems && (
                      <div className={cn("absolute top-full left-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50", { 'px-4': item.externalLinks })}>
                        {item.dropdownItems.map((dropItem, idx) => (
                          <Link
                            key={idx}
                            to={dropItem.path}
                            className={cn(`group block px-4 py-2 text-sm transition-colors ${
                              location.pathname === dropItem.path
                                ? 'text-thopen-pink bg-pink-50'
                                : 'text-gray-700 hover:text-thopen-pink hover:bg-gray-50'
                            }`, { 'text-base border rounded-xl border-gray-100 hover:border-thopen-pink mb-2 py-5 flex flex-col': dropItem.target })}
                            target={dropItem.target}
                            rel={dropItem.rel}
                            onClick={() => setClickedMenu(null)}
                          >
                            {dropItem.name}
                            <div className={cn('flex items-center', { hidden: !dropItem.target })}>
                              <span className="text-sm">Saiba mais</span>
                              <CircleChevronRight
                                className="w-4 h-4 ml-2 opacity-0 -translate-x-5 transition-all duration-300 ease-out group-hover:opacity-100 group-hover:translate-x-0"
                                  
                              />
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => {
                      if (clickedMenu === item.name) {
                        setClickedMenu(null);
                      } else {
                        setClickedMenu(item.name);
                      }
                    }}
                    className={`flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      clickedMenu === item.name
                        ? 'text-thopen-pink bg-pink-50'
                        : 'text-gray-700 hover:text-thopen-pink hover:bg-gray-50'
                    }`}
                  >
                    {item.name}
                    <ChevronDown className="ml-1 h-4 w-4" />
                  </button>
                )}

                {/* Mega Menu Dropdown for Soluções */}
                {!item.simple && clickedMenu === item.name && item.submenu && (
                  <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-6 z-50">
                    <div className="px-6 py-2 border-b border-gray-100 mb-4">
                      <h3 className="font-semibold text-gray-900">{item.name}</h3>
                    </div>
                    
                    <Tabs defaultValue="o-que-precisa" className="w-full">
                      <div className="flex items-stretch">
                        {/* Tabs List - Vertical */}
                        <TabsList className="flex-col h-full self-stretch w-60 bg-gray-50 p-2 mr-6 items-start">
                          {Object.entries(item.submenu.categories).map(([key, category]) => (
                            <TabsTrigger 
                              key={key} 
                              value={key}
                              className="w-full justify-start text-left py-3 px-4 data-[state=active]:bg-white data-[state=active]:text-thopen-pink"
                            >
                              {category.name}
                            </TabsTrigger>
                          ))}
                        </TabsList>

                        {/* Tabs Content */}
                        <div className="w-max bg-white">
                          {Object.entries(item.submenu.categories).map(([key, category]) => (
                            <TabsContent key={key} value={key} className="mt-0">
                              <div className={cn(`grid grid-cols-1 md:grid-cols-${category.items.length} gap-6 pr-6`)}>
                                {category.items.map((section: any, sectionIndex: number) => (
                                  <div key={sectionIndex}>
                                    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                                      {section.name}
                                    </h4>
                                    <div className="space-y-2">
                                      {section.items.map((subItem: any, idx: number) => (
                                        <Link
                                          key={idx}
                                          to={subItem.path}
                                          className="block rounded-md p-3 hover:bg-gray-50 transition-colors w-[254px]"
                                          onClick={() => setClickedMenu(null)}
                                        >
                                          <div className="font-semibold text-gray-900 text-sm">{subItem.name}</div>
                                          <p className="text-xs text-gray-600 mt-1 leading-snug">{getMicrocopy(subItem.name)}</p>
                                        </Link>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </TabsContent>
                          ))}
                        </div>
                      </div>
                    </Tabs>
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-1.5 shrink-0">
            {location.pathname !== '/' && location.pathname !== '/solucoes/o-que-voce-precisa/quero-economizar-na-conta-de-luz/simule-sua-economia' && (
              <Link
                to="/solucoes/o-que-voce-precisa/quero-economizar-na-conta-de-luz/simule-sua-economia"
                className="bg-gradient-to-r from-thopen-pink to-thopen-yellow text-white px-4 py-2 rounded-lg font-display font-semibold hover:shadow-lg transition-all duration-200 flex items-center gap-2 whitespace-nowrap shrink-0"
              >
                <Calculator className="h-4 w-4 shrink-0" />
                <span>Calcule sua Economia</span>
              </Link>
            )}
            <a
              href={minhaThopentHref}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center p-2 rounded-md border border-gray-300 text-gray-700 hover:border-thopen-pink hover:text-thopen-pink transition-colors"
              aria-label="Área do associado"
            >
              <UserRound className="h-4 w-4" />
            </a>
            <a
              href={areaAgenteHref}
              target={areaAgenteHref !== "#" ? "_blank" : undefined}
              rel={areaAgenteHref !== "#" ? "noopener noreferrer" : undefined}
              className="flex items-center p-2 rounded-md border border-gray-300 text-gray-700 hover:border-thopen-pink hover:text-thopen-pink transition-colors"
              aria-label="Agente Thopen"
            >
              <Briefcase className="h-4 w-4" />
            </a>
          </div>

          {/* Mobile: calcule button + hamburger */}
          <div className="lg:hidden flex items-center gap-2">
            {location.pathname !== '/' && location.pathname !== '/solucoes/o-que-voce-precisa/quero-economizar-na-conta-de-luz/simule-sua-economia' && (
              <Link
                to="/solucoes/o-que-voce-precisa/quero-economizar-na-conta-de-luz/simule-sua-economia"
                className="bg-gradient-to-r from-thopen-pink to-thopen-yellow text-white px-3 py-2 rounded-lg font-display font-semibold text-sm flex items-center gap-1.5 whitespace-nowrap shrink-0"
              >
                <Calculator className="h-4 w-4 shrink-0" />
                <span>Calcule sua Economia</span>
              </Link>
            )}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 rounded-md text-gray-700 hover:text-thopen-pink hover:bg-gray-50"
              aria-label={isMenuOpen ? "Fechar menu" : "Abrir menu"}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 py-4 max-h-[calc(100vh-5rem)] overflow-y-auto">
            <Accordion type="multiple" className="w-full">
              {menuItems.map((item, itemIndex) => (
                <AccordionItem key={item.name} value={`item-${itemIndex}`} className="border-b-0">
                  {item.simple ? (
                    <Link
                      to={item.path}
                      className={`flex items-center px-4 py-3 text-base font-medium ${
                        location.pathname === item.path
                          ? 'text-thopen-pink bg-pink-50'
                          : 'text-gray-900 hover:text-thopen-pink hover:bg-gray-50'
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.name}
                    </Link>
                  ) : item.submenu ? (
                    <>
                      <AccordionTrigger className="px-4 py-3 text-base font-semibold text-gray-900 hover:text-thopen-pink hover:no-underline [&[data-state=open]>svg]:rotate-180">
                        {item.name}
                      </AccordionTrigger>
                      <AccordionContent className="pb-0">
                        <Accordion type="multiple" className="w-full">
                          {Object.entries(item.submenu.categories).map(([key, category], catIndex) => (
                            <AccordionItem key={key} value={`cat-${itemIndex}-${catIndex}`} className="border-b-0">
                              <AccordionTrigger className="px-6 py-2.5 text-sm font-medium text-gray-700 hover:text-thopen-pink hover:no-underline bg-gray-50/50">
                                {category.name}
                              </AccordionTrigger>
                              <AccordionContent className="pb-0">
                                {category.items.map((section: any, sectionIndex: number) => (
                                  <div key={sectionIndex} className="py-2">
                                    <div className="px-8 py-1.5 text-xs font-semibold uppercase tracking-wide text-gray-500">
                                      {section.name}
                                    </div>
                                    <div className="space-y-0.5">
                                      {section.items.map((subItem: any, subIndex: number) => (
                                        <Link
                                          key={subIndex}
                                          to={subItem.path}
                                          className={`flex items-center px-8 py-2.5 text-sm ${
                                            location.pathname === subItem.path
                                              ? 'text-thopen-pink bg-pink-50/50'
                                              : 'text-gray-600 hover:text-thopen-pink hover:bg-gray-50'
                                          }`}
                                          onClick={() => setIsMenuOpen(false)}
                                        >
                                          <ChevronRight className="h-3 w-3 mr-2 text-gray-400" />
                                          {subItem.name}
                                        </Link>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </AccordionContent>
                            </AccordionItem>
                          ))}
                        </Accordion>
                      </AccordionContent>
                    </>
                  ) : item.hasDropdown && item.dropdownItems ? (
                    <>
                      <AccordionTrigger className="px-4 py-3 text-base font-semibold text-gray-900 hover:text-thopen-pink hover:no-underline [&[data-state=open]>svg]:rotate-180">
                        {item.name}
                      </AccordionTrigger>
                      <AccordionContent className="pb-2">
                        <div className="space-y-0.5">
                          {item.dropdownItems.map((dropItem, idx) => (
                            dropItem.target === '_blank' ? (
                              <a
                                key={idx}
                                href={dropItem.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center justify-between px-6 py-2.5 text-sm text-gray-600 hover:text-thopen-pink hover:bg-gray-50`}
                                onClick={() => setIsMenuOpen(false)}
                              >
                                <span className="flex items-center">
                                  <ChevronRight className="h-3 w-3 mr-2 text-gray-400" />
                                  {dropItem.name}
                                </span>
                                <ExternalLink className="h-3 w-3 text-gray-400" />
                              </a>
                            ) : (
                              <Link
                                key={idx}
                                to={dropItem.path}
                                className={`flex items-center px-6 py-2.5 text-sm ${
                                  location.pathname === dropItem.path
                                    ? 'text-thopen-pink bg-pink-50/50'
                                    : 'text-gray-600 hover:text-thopen-pink hover:bg-gray-50'
                                }`}
                                onClick={() => setIsMenuOpen(false)}
                              >
                                <ChevronRight className="h-3 w-3 mr-2 text-gray-400" />
                                {dropItem.name}
                              </Link>
                            )
                          ))}
                        </div>
                      </AccordionContent>
                    </>
                  ) : null}
                </AccordionItem>
              ))}
            </Accordion>
            
            {/* CTA Buttons */}
            <div className="pt-4 mt-4 border-t border-gray-200 px-4 space-y-3">
              {location.pathname !== '/' && location.pathname !== '/solucoes/o-que-voce-precisa/quero-economizar-na-conta-de-luz/simule-sua-economia' && (
                <Link
                  to="/solucoes/o-que-voce-precisa/quero-economizar-na-conta-de-luz/simule-sua-economia"
                  className="flex items-center justify-center gap-2 bg-gradient-to-r from-thopen-pink to-thopen-yellow text-white px-6 py-3 rounded-lg font-display font-semibold text-center shadow-md"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Calculator className="h-4 w-4" />
                  Calcule sua Economia
                </Link>
              )}
              <a
                href={minhaThopentHref}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 text-gray-700 hover:text-thopen-pink px-4 py-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                <UserRound className="h-4 w-4 shrink-0" />
                Área do associado
                <ExternalLink className="h-3.5 w-3.5 ml-1 text-gray-400" />
              </a>
              <a
                href={areaAgenteHref}
                target={areaAgenteHref !== "#" ? "_blank" : undefined}
                rel={areaAgenteHref !== "#" ? "noopener noreferrer" : undefined}
                className="flex items-center justify-center gap-2 text-gray-700 hover:text-thopen-pink px-4 py-2.5 rounded-lg border border-gray-200 hover:bg-gray-50 font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                <Briefcase className="h-4 w-4 shrink-0" />
                Agente Thopen
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
